import { useEffect, useMemo, useState } from 'react';
import type { TaskPriority, TaskStatus } from '@forecastify/shared';
import { Button, Dialog, Select, TextInput, Textarea } from '@forecastify/ui';
import { useT } from '../../i18n';
import { Field } from '../card';
import { DateField } from '../date-field';

/**
 * The new-task dialog — `markup.html` 2603+.
 *
 * ⚠️ **No key field, because there is no key to type.** It comes from the project's prefix through
 * a counter that only the server can advance; the description says so, exactly as the mockup's
 * does: *"A kulcsot a projekt prefixéből kapja."*
 *
 * ⚠️ **The assignee list is the PROJECT'S members** — the caller supplies it, and P-1 is why. See
 * `task-detail.tsx`'s note: offering a name the server will refuse is worse than not offering it.
 */
export interface TaskDraft {
  title: string;
  description: string;
  statusId: string;
  priorityId: string;
  assigneeMemberId: string;
  /**
   * ⚠️⚠️ **WHICH PROJECT, AND IT WAS ABSENT FROM THE TASK LIST'S DIALOG ENTIRELY.** Creating a
   * task from Feladatok made one belonging to no project — no key prefix, no line item, no place in
   * any project's figures — and the only way to attach it was to open the task afterwards. Empty
   * string is *none*, which stays an ordinary answer: a task without a project is legal.
   */
  projectId: string;
  /**
   * ⚠️⚠️ **C25's field, and it was missing from CREATION alone.** The task detail has had a
   * line-item picker since C25; this dialog never did, so the only way to attach one was to create
   * the task and then open it. Empty string is *none*, which is an ordinary answer — a task with
   * no line item is allowed everywhere (P2-4, reversed 8 Sep 2026).
   */
  lineItemId: string;
  estimateHours: string;
  startsOn: string;
  dueOn: string;
}

/**
 * ⚠️⚠️ **CARRIES ITS PROJECT, BECAUSE THE DIALOG IS NOW THE THING THAT NARROWS.** The caller used
 * to hand over a list already filtered, which worked while the project was fixed — on the task list
 * the project is CHOSEN in this dialog, so the filtering has to happen against a value that only
 * exists here. A line item reaches a project through its contract, and offering another project's
 * would put the hours on the wrong client's budget.
 */
export interface TaskDialogLineItem {
  readonly id: string;
  readonly name: string;
  readonly contractCode: string;
  readonly projectId: string | null;
  /** 30 Oct 2026 · closed: drawn, unpickable, and saying so (`line_item_closed`). */
  readonly closed?: boolean;
}

export function TaskDialog({
  open,
  statuses,
  priorities,
  assignees,
  projects = [],
  projectId = '',
  lineItems = [],
  onClose,
  onSubmit,
  serverError,
}: {
  readonly open: boolean;
  readonly statuses: readonly TaskStatus[];
  readonly priorities: readonly TaskPriority[];
  readonly assignees: readonly { id: string; name: string }[];
  /**
   * ⚠️⚠️ **NON-EMPTY MEANS THE PROJECT IS CHOSEN HERE; EMPTY MEANS IT IS ALREADY DECIDED.** The
   * task list creates a task anywhere in the workspace, so it passes the list and gets a picker; a
   * project's own tab passes nothing and `projectId` instead, because the answer is not in question.
   */
  readonly projects?: readonly { readonly id: string; readonly name: string }[];
  /** ⚠️ The project a fixed-project caller is creating in. Ignored where `projects` is offered. */
  readonly projectId?: string;
  /**
   * ⚠️ **The WHOLE workspace's, unfiltered** — this dialog narrows them by the chosen project,
   * because on the task list that choice is made here. Empty draws no picker at all, which is the
   * right answer for a tenant without contracts and for a project with no line items yet.
   */
  readonly lineItems?: readonly TaskDialogLineItem[];
  readonly onClose: () => void;
  readonly onSubmit: (draft: TaskDraft) => void;
  readonly serverError: string | null;
}) {
  const t = useT();
  const [draft, setDraft] = useState<TaskDraft>(empty);
  const [error, setError] = useState<string | null>(null);

  /* Reset on open, so a dismissed draft does not reappear in the next task's form. */
  useEffect(() => {
    if (!open) return;
    setError(null);
    setDraft({
      ...empty(),
      /* ⚠️ Pre-selected from the workspace's defaults rather than left blank: a blank status is a
       * choice the person has to make before they can write down what they were thinking about. */
      statusId: statuses.find((status) => status.isDefault)?.id ?? statuses[0]?.id ?? '',
      priorityId: priorities.find((priority) => priority.isDefault)?.id ?? priorities[0]?.id ?? '',
      /* ⚠️ Where the project is not in question, it is seeded here — which is also what makes the
         line-item list below narrow correctly without a second notion of *which project*. */
      projectId,
    });
  }, [open, statuses, priorities, projectId]);

  /**
   * ⚠️⚠️ **NARROWED BY THE CHOSEN PROJECT, NOT BY THE CALLER.** A line item belongs to a project
   * through its contract (`contracts.project_id` is `not null`), so one from elsewhere would put
   * these hours on another client's budget — the server refuses it as `line_item_not_on_project`,
   * a refusal nobody should be shown because nothing should have offered it. With no project chosen
   * there is nothing to offer, which is why the picker disappears again when the project is cleared.
   */
  const onProject = useMemo(
    () =>
      draft.projectId === '' ? [] : lineItems.filter((item) => item.projectId === draft.projectId),
    [lineItems, draft.projectId],
  );

  const patch = (part: Partial<TaskDraft>): void => {
    setDraft((current) => ({ ...current, ...part }));
    setError(null);
  };

  const submit = (): void => {
    if (draft.title.trim() === '') {
      setError(t('task.error.title'));
      return;
    }
    /* ⚠️ P2-32.2 · every task is created on a line item; the server refuses one without. */
    if (draft.lineItemId === '') {
      setError(t('task.error.lineItem'));
      return;
    }
    onSubmit(draft);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('task.dialog.new')}
      description={t('task.dialog.newHint')}
      closeLabel={t('common.close')}
    >
      <div className="flex flex-col gap-3">
        <Field required label={t('task.field.title')}>
          <TextInput
            value={draft.title}
            autoFocus
            placeholder={t('task.dialog.titlePlaceholder')}
            onChange={(event) => {
              patch({ title: event.target.value });
            }}
          />
        </Field>

        <Field label={t('task.field.description')}>
          <Textarea
            value={draft.description}
            rows={3}
            onChange={(event) => {
              patch({ description: event.target.value });
            }}
          />
        </Field>

        {/* ⚠️ Full width, under the description — a line item's name is long, and half a dialog
            cut it down to the contract code. The project, where it is offered, comes first: the
            line items are that project's. */}
        {/**
         * ⚠️⚠️ **THE TASK LIST COULD NOT PUT A TASK IN A PROJECT AT ALL.** Creating one from
         * Feladatok made a project-less task — no key prefix, no line item, absent from every
         * project figure — and the only way to place it was to open it afterwards and edit.
         *
         * ⚠️ **Absent where the project is not in question**, which is the project's own tab: it
         * passes no list and a `projectId` instead, so there is no control offering to move a
         * task out of the project whose page it was created on.
         *
         * ⚠️⚠️ **CHOOSING A PROJECT MAKES THE ASSIGNEE A P-1 QUESTION.** Membership grants the
         * right to be assigned, and this list is the WORKSPACE's — so a person who is not on the
         * chosen project is refused by the server as `assignee_not_a_project_member`, named, with
         * the dialog left open. Filtering the list here would need that project's membership,
         * which is a fetch per selection; the refusal is honest and says who.
         */}
        {projects.length === 0 ? null : (
          <Field label={t('task.field.project')}>
            <Select
              /* U-14 · a list of records, so it searches. */
              searchable
              searchPlaceholder={t('list.searchOptions')}
              value={draft.projectId}
              aria-label={t('task.field.project')}
              onChange={(event) => {
                /* ⚠️ The line item goes with it — it belonged to the OLD project's contract, and
                   carrying it over is exactly the cross-project pairing the server refuses. */
                patch({ projectId: event.target.value, lineItemId: '' });
              }}
              /* ⚠️ P2-32.2 · no "none": a task's project comes from its line item, so a task
                 with no project cannot exist. The empty value is only "not chosen yet". */
              options={[
                { value: '', label: t('common.choose'), disabled: true },
                ...projects.map((project) => ({ value: project.id, label: project.name })),
              ]}
            />
          </Field>
        )}

        {/**
         * ⚠️⚠️ **ALWAYS DRAWN, AND REQUIRED — P2-32.2.** Every task is created on a line item and
         * its project is that line item's. The list is the chosen project's work line items; a
         * project with none says so, because nothing can be created on it until it has one.
         */}
        <Field label={t('task.field.lineItem')}>
          {draft.projectId !== '' && onProject.length === 0 ? (
            <p className="text-muted-foreground text-[12px]">{t('task.field.lineItemNone')}</p>
          ) : (
            <Select
              /* U-14 · a list of records, so it searches. */
              searchable
              searchPlaceholder={t('list.searchOptions')}
              value={draft.lineItemId}
              aria-label={t('task.field.lineItem')}
              disabled={draft.projectId === ''}
              onChange={(event) => {
                patch({ lineItemId: event.target.value });
              }}
              options={[
                { value: '', label: t('common.choose'), disabled: true },
                /* ⚠️ The line item's NAME first and the contract after it. The code still tells two
                   *Havi támogatás* apart, but leading with it put the same code at the start of
                   every row and truncated the name somebody is choosing by. */
                ...onProject.map((item) => ({
                  value: item.id,
                  label:
                    item.closed === true
                      ? t('lineItem.option.closed', {
                          label: `${item.name} · ${item.contractCode}`,
                        })
                      : `${item.name} · ${item.contractCode}`,
                  disabled: item.closed === true,
                })),
              ]}
            />
          )}
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('task.field.status')}>
            <Select
              value={draft.statusId}
              onChange={(event) => {
                patch({ statusId: event.target.value });
              }}
              options={statuses.map((status) => ({ value: status.id, label: status.name }))}
            />
          </Field>

          <Field label={t('task.field.priority')}>
            <Select
              value={draft.priorityId}
              onChange={(event) => {
                patch({ priorityId: event.target.value });
              }}
              options={priorities.map((priority) => ({
                value: priority.id,
                label: priority.name,
              }))}
            />
          </Field>

          <Field label={t('task.field.assignee')}>
            <Select
              /* U-14 · a list of records, so it searches. */
              searchable
              searchPlaceholder={t('list.searchOptions')}
              value={draft.assigneeMemberId}
              onChange={(event) => {
                patch({ assigneeMemberId: event.target.value });
              }}
              options={[
                { value: '', label: t('common.none') },
                ...assignees.map((person) => ({ value: person.id, label: person.name })),
              ]}
            />
          </Field>

          <Field label={t('task.field.estimate')} note={t('task.field.estimateHint')}>
            <TextInput
              value={draft.estimateHours}
              placeholder="8:00"
              onChange={(event) => {
                patch({ estimateHours: event.target.value });
              }}
            />
          </Field>

          <Field label={t('task.field.start')}>
            <DateField
              label={t('task.field.start')}
              value={draft.startsOn}
              onChange={(startsOn) => {
                patch({ startsOn });
              }}
            />
          </Field>

          <Field label={t('task.field.due')}>
            <DateField
              label={t('task.field.due')}
              value={draft.dueOn}
              onChange={(dueOn) => {
                patch({ dueOn });
              }}
            />
          </Field>
        </div>

        {error === null && serverError === null ? null : (
          <p className="text-destructive text-[12px]" role="alert">
            {error ?? serverError}
          </p>
        )}

        <div className="mt-1 flex items-center gap-2">
          <div className="flex-1" />
          <Button variant="outline" size="lg" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button size="lg" onClick={submit}>
            {t('task.action.create')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function empty(): TaskDraft {
  return {
    title: '',
    description: '',
    statusId: '',
    priorityId: '',
    assigneeMemberId: '',
    projectId: '',
    lineItemId: '',
    estimateHours: '',
    startsOn: '',
    dueOn: '',
  };
}
